import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { describeError, isApiError, isUnauthenticated } from './api';

/** 已经以 toast 呈现过的错误：避免重复提示，也用于吞掉对应的 unhandledrejection */
const toasted = new WeakSet<object>();

export function markToasted(err: unknown) {
  if (err && typeof err === 'object') toasted.add(err);
}
export function wasToasted(err: unknown): boolean {
  return Boolean(err && typeof err === 'object' && toasted.has(err));
}

export function toastError(err: unknown, fallbackTitle?: string) {
  if (wasToasted(err)) return;
  const { title, description } = describeError(err);
  toast.error(fallbackTitle ? `${fallbackTitle}：${title}` : title, { description });
  markToasted(err);
}

let onUnauthenticated: (() => void) | null = null;
export function setUnauthenticatedHandler(fn: () => void) {
  onUnauthenticated = fn;
}

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      silent?: boolean;
      errorTitle?: string;
      /** 登录流程自身处理 401，不触发跳转 */ authFlow?: boolean;
    };
    queryMeta: { silent?: boolean };
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (err, query) => {
      if (isUnauthenticated(err) && query.queryKey[0] !== 'me') {
        onUnauthenticated?.();
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (err, _vars, _ctx, mutation) => {
      if (isUnauthenticated(err) && !mutation.meta?.authFlow) {
        onUnauthenticated?.();
        return;
      }
      if (mutation.meta?.silent) return;
      toastError(err, mutation.meta?.errorTitle);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 20_000,
      refetchOnWindowFocus: true,
      retry: (count, err) => {
        if (isApiError(err) && err.status > 0 && err.status < 500) return false;
        return count < 2;
      },
    },
  },
});
